import { LexicalNode } from 'lexical';
import { $isParagraphNode } from 'lexical';
import { $isHeadingNode } from '@lexical/rich-text';

export type PlacementContext = {
  node: LexicalNode;
  nextNode: LexicalNode | null;
  wordsSinceLastAd: number;
  nodesSinceLastAd: number;
  totalWordCount: number;
  totalNodeCount: number;
  adCount: number;
  isLastNode: boolean;
};

export type PlacementRule = {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  priority: number;
  evaluate: (context: PlacementContext) => {
    shouldPlace: boolean;
    reason: string;
    priority: number;
  };
};

export class LexicalAdRulesEngine {
  private rules: PlacementRule[] = [];
  public globalConfig = {
    minAdsPerArticle: 1,
    maxAdsPerArticle: 3,
    minWordsBetweenAds: 500,
    minNodesBetweenAds: 4
  };

  constructor(config = {}) {
    this.globalConfig = { ...this.globalConfig, ...config };
    this.initializeDefaultRules();
  }

  private initializeDefaultRules() {
    // Guaranteed Ad Rule
    this.addRule({
      id: 'guaranteed-ad',
      name: 'Guaranteed Ad Rule',
      description: 'Ensures at least one ad appears in each article',
      enabled: true,
      priority: 200, // Highest priority
      evaluate: (context: PlacementContext) => ({
        shouldPlace:
          context.adCount === 0 &&
          // Place if we're near the middle of the article or at the last node
          ((context.totalNodeCount > 0 &&
            context.nodesSinceLastAd >= Math.floor(context.totalNodeCount / 2)) ||
            context.isLastNode),
        reason: 'Guaranteed minimum ad placement',
        priority: 200
      })
    });

    // Section Break Rule
    this.addRule({
      id: 'section-break',
      name: 'Section Break Rule',
      description: 'Places ads before new section headings',
      enabled: true,
      priority: 100,
      evaluate: (context: PlacementContext) => ({
        shouldPlace:
          context.nextNode &&
          $isHeadingNode(context.nextNode) &&
          context.wordsSinceLastAd >= this.globalConfig.minWordsBetweenAds &&
          context.adCount < this.globalConfig.maxAdsPerArticle,
        reason: 'New section break',
        priority: 100
      })
    });

    // Paragraph Group Rule
    this.addRule({
      id: 'paragraph-group',
      name: 'Paragraph Group Rule',
      description: 'Places ads after groups of paragraphs',
      enabled: true,
      priority: 80,
      evaluate: (context: PlacementContext) => ({
        shouldPlace:
          $isParagraphNode(context.node) &&
          context.nodesSinceLastAd >= this.globalConfig.minNodesBetweenAds &&
          context.wordsSinceLastAd >= this.globalConfig.minWordsBetweenAds &&
          context.adCount < this.globalConfig.maxAdsPerArticle,
        reason: 'Paragraph group break',
        priority: 80
      })
    });
  }

  addRule(rule: PlacementRule): void {
    this.rules.push(rule);
  }

  evaluateRules(context: PlacementContext): { shouldPlace: boolean; reason: string | null } {
    const applicableRules = this.rules
      .filter((rule) => rule.enabled)
      .sort((a, b) => b.priority - a.priority);

    for (const rule of applicableRules) {
      const result = rule.evaluate(context);

      if (result.shouldPlace) {
        return { shouldPlace: true, reason: result.reason };
      }
    }

    return { shouldPlace: false, reason: null };
  }
}
